import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalContent, Button } from "component-library";
import { DownloadReportResult } from "../hooks/useDownloadReport";

interface DownloadModalProps {
  reportData: DownloadReportResult;
  onClose: () => void;
}

export const DownloadModal = ({ reportData, onClose }: DownloadModalProps) => {
  const { t } = useTranslation();
  const fileSizeMB = (reportData.size / 1024 / 1024).toFixed(2);

  const handleDownload = useCallback(() => {
    const url = URL.createObjectURL(reportData.blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = reportData.fileName;
    link.click();

    setTimeout(() => URL.revokeObjectURL(url), 100);

    setTimeout(() => onClose(), 50);
  }, [reportData.blob, reportData.fileName, onClose]);

  const handlePreview = useCallback(() => {
    const url = URL.createObjectURL(reportData.blob);
    window.open(url, "_blank");

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [reportData.blob]);

  return (
    <Modal open={true} handleClose={onClose} withXButton={true}>
      <ModalContent>
        <div className="max-w-md w-full">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-6">
            {t("dashboard.downloadModal.title")}
          </h2>

          <div className="mb-6">
            <div className="mb-2">
              <span className="text-gray-700 font-medium">
                {reportData.fileName}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              {t("dashboard.downloadModal.fileSize", { size: fileSizeMB })}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outlined"
              className="flex-1"
              onClick={handlePreview}
            >
              {t("dashboard.downloadModal.preview")}
            </Button>

            <Button
              variant="contained"
              className="flex-1"
              onClick={handleDownload}
            >
              {t("dashboard.downloadModal.download")}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
